import { useEffect, useRef, useState, useSyncExternalStore } from 'react';
import { Input, type InputProps } from '../../../../../components/ui/Input';
import { InputNumber, type InputNumberProps } from '../../../../../components/ui/InputNumber';
import { Link } from '../../../../../components/ui/Link';
import styles from './OrderPreviewDialogV2.module.css';

import type { PendingEdits } from './pending-order-edits';
interface Props extends Omit<InputProps, 'onValueChange' | 'onBlur' | 'onFocus'> {
  editValue?: string;
  editKey: string;
  pending: PendingEdits;
  onCommit: (value: string) => void;
}

// Business-page adapter: typing stays local; only complete edit checkpoints reach
// the order state. The actual control remains the registered local Input.
export function BufferedOrderInput({ editKey, editValue, pending, onCommit, value, suffix, ...props }: Props) {
  const [draft, setDraft] = useState<string | null>(null);
  const text = useRef(value);
  const dirty = useRef(false);
  const composing = useRef(false);
  const [focused, setFocused] = useState(false);
  function flush() {
    if (!dirty.current) return;
    dirty.current = false;
    onCommit(text.current);
    pending.remove(editKey);
  }
  useEffect(() => () => { pending.remove(editKey); }, [editKey, pending]);
  const displayed = draft ?? value;
  return <Input {...props} value={displayed} suffix={!focused && displayed ? suffix : undefined}
    onValueChange={next => {
      text.current = next;
      dirty.current = true;
      setDraft(next);
      pending.set(editKey, flush);
    }}
    onFocus={() => { setFocused(true); setDraft(editValue ?? value); }}
    onCompositionStart={() => { composing.current = true; }}
    onCompositionEnd={() => { composing.current = false; }}
    onBlur={() => { flush(); setDraft(null); setFocused(false); }}
    onPressEnter={event => { if (!composing.current) { flush(); event.currentTarget.blur(); } }} />;
}

interface QuantityProps extends Omit<InputNumberProps, 'onValueChange'> {
  editKey: string;
  pending: PendingEdits;
  onCommit: (value: number | null) => void;
}
export function BufferedOrderQuantity({ editKey, pending, onCommit, value, ...props }: QuantityProps) {
  const [draft, setDraft] = useState<number | null | undefined>(undefined);
  const text = useRef(value);
  const dirty = useRef(false);
  function flush() {
    if (!dirty.current) return;
    dirty.current = false;
    onCommit(text.current);
    pending.remove(editKey);
  }
  useEffect(() => () => { pending.remove(editKey); }, [editKey, pending]);
  return <div className={styles.quantityEditor}
    onBlur={event => {
      if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) return;
      flush();
      setDraft(undefined);
    }}
    onKeyDown={event => { if (event.key === 'Enter' && event.target instanceof HTMLElement) { flush(); event.target.blur(); } }}>
    <InputNumber {...props} value={draft === undefined ? value : draft} onValueChange={next => {
      text.current = next;
      dirty.current = true;
      setDraft(next);
      pending.set(editKey, flush);
    }} />
  </div>;
}

export function OrderSaveStatus({ pending, state, savedAt, retry }: {
  pending: PendingEdits; state: 'idle' | 'saving' | 'saved' | 'failed'; savedAt?: number; retry: () => unknown;
}) {
  const editing = useSyncExternalStore(pending.subscribe, pending.getSnapshot, pending.getSnapshot);
  const status = editing ? 'editing' : state;
  return <div aria-live="polite" className={styles.saveStatus} data-failed={status === 'failed' || undefined}>
    {status === 'editing' ? '编辑中' : status === 'saving' ? '保存中…' : status === 'saved' ? `已保存 · ${new Date(savedAt ?? 0).toLocaleTimeString('zh-CN', { hour: '2-digit', minute: '2-digit', hour12: false })}` : status === 'failed' ? '保存失败' : '修改自动保存'}
    {status === 'failed' ? <Link onClick={() => { retry(); }}>重试</Link> : null}
  </div>;
}
